"use client";

import { useEffect, useRef } from "react";

import { usePathname } from "next/navigation";

import { setTealiumData, usePageDataMap } from "@repo/analytics";

import { utagReady } from "@repo/ui/lib/utagReady";
import { useLocalizedPathname } from "@repo/ui/navigation";

import { useBookingDetails, useIsCheckin, useIsPostBooking } from "@repo/store/slices";

const INIT_PAGES = [
  "",
  "flight",
  "baggage",
  "seats",
  "extras",
  "passengers",
  "payment",
  "itinerary",
];

type TealiumView = (data: Record<string, string>) => void;

interface UseTealiumPageViewArgs {
  /** Canonical culture for the current render, e.g. `es-CL`. Resolve it on the server. */
  culture: string;
  /**
   * The app's `tealiumView` helper. Pass a stable (module-level) reference — an inline
   * arrow re-runs the effect on every render, which the dedupe guard absorbs but which
   * is wasted work.
   */
  view: TealiumView;
  enabled?: boolean;
}

/**
 * Emits a `utag.view` on every page load — hard reloads and client-side navigations
 * alike — carrying the current pathname and culture.
 *
 * Waits on {@link utagReady} first, so the initial view of a hard load is queued
 * rather than dropped while utag.js is still in flight.
 */
export function useTealiumPageView({ culture, view, enabled = true }: UseTealiumPageViewArgs) {
  const pathname = usePathname();
  const localizedPathname = useLocalizedPathname();
  const lastFired = useRef<string | null>(null);

  const pageDataBuilder = usePageDataMap(localizedPathname);

  const isCheckinFlow = useIsCheckin();
  const isPostBooking = useIsPostBooking();
  const isModifyBooking = window.location.pathname.includes("/modify-booking/");
  const isBookingFlow = !isCheckinFlow && !isPostBooking && !isModifyBooking;

  const bookingDetails = useBookingDetails();
  const page = localizedPathname.split("/").at(-1) ?? "";
  const isItineraryPage = page === "itinerary";
  const ready = isItineraryPage ? !!bookingDetails?.recordLocator : true;

  useEffect(() => {
    if (!enabled || !pathname || !culture || !ready) return;

    // Culture is part of the key so a mid-session market change re-fires the view: the
    // page has effectively reloaded in a different market.
    const key = `${pathname}|${culture}`;
    if (lastFired.current === key) return;

    let cancelled = false;

    void utagReady().then((loaded) => {
      // Bail if the user navigated away while utag.js was still loading, or if it
      // never loaded at all (blocked, offline, timed out).
      if (cancelled || !loaded) return;
      if (lastFired.current === key) return;

      lastFired.current = key;

      const page = localizedPathname.split("/").at(-1) ?? "";

      if (pageDataBuilder && (isBookingFlow || INIT_PAGES.includes(page))) {
        setTealiumData(pageDataBuilder());
      }

      view({
        currentPathname: pathname,
        currentCulture: culture,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [pathname, culture, enabled, ready, view, pageDataBuilder, localizedPathname, isBookingFlow]);
}
